import { Fragment, FunctionComponent } from 'react';
import { Box, Button, Card, Divider, Stack, Typography } from '@mui/material';
import { DrawerActions } from '@/components/Drawer/DrawerActions';
import { Coupon } from '@/models/coupon';

import { getMoneyAsString } from '@/utils/money';
import { formatStarjackDate } from '@/utils/time';
import { CardListRow } from '@/components/Card/CardListRow';

type CouponDrawerContentProps = {
    coupon: Coupon;
    onClose: () => void;
};

export const CouponEditDrawerContent: FunctionComponent<
    CouponDrawerContentProps
> = ({ coupon, onClose }) => {
    return (
        <Box
            sx={{
                width: '720px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>Coupon Details</Typography>
            </Box>

            <Stack
                sx={{
                    p: 4,
                }}
                spacing={4}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 500,
                        }}
                    >
                        Name
                    </Typography>

                    <Typography
                        sx={{
                            mt: 2,
                            ml: 2,
                        }}
                    >
                        {coupon.name}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        sx={{
                            fontWeight: 500,
                        }}
                    >
                        Betrag
                    </Typography>

                    <Typography
                        sx={{
                            mt: 2,
                            ml: 2,
                        }}
                    >
                        {getMoneyAsString(coupon.amount)}
                    </Typography>
                </Box>

                {!!coupon.validFrom && (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 500,
                            }}
                        >
                            Gültig ab
                        </Typography>

                        <Typography
                            sx={{
                                mt: 2,
                                ml: 2,
                            }}
                        >
                            {formatStarjackDate(coupon.validFrom)}
                        </Typography>
                    </Box>
                )}

                <Box>
                    <Typography
                        sx={{
                            fontWeight: 500,
                        }}
                    >
                        Gültig bis
                    </Typography>

                    <Typography
                        sx={{
                            mt: 2,
                            ml: 2,
                        }}
                    >
                        {formatStarjackDate(coupon.validUntil)}
                    </Typography>
                </Box>

                <Card elevation={2}>
                    <CardListRow
                        sx={{
                            backgroundColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography sx={{ width: '33%', fontWeight: 600 }}>
                            Code
                        </Typography>
                        <Typography sx={{ width: '33%', fontWeight: 600 }}>
                            Contingent
                        </Typography>

                        <Typography sx={{ width: '33%', fontWeight: 600 }}>
                            Verbraucht
                        </Typography>
                    </CardListRow>

                    {coupon.codes.map((code, index) => (
                        <Fragment key={code.id}>
                            {index !== 0 && <Divider sx={{ width: '100%' }} />}
                            <CardListRow
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Typography sx={{ width: '33%' }}>
                                    {code.code}
                                </Typography>

                                <Typography sx={{ width: '33%' }}>
                                    {code.contingent}
                                </Typography>

                                <Typography sx={{ width: '33%' }}>
                                    {code.used}
                                </Typography>
                            </CardListRow>
                        </Fragment>
                    ))}
                </Card>

                <DrawerActions>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                        spacing={2}
                        direction='row'
                    >
                        <Button variant='outlined' onClick={onClose}>
                            Abbrechen
                        </Button>
                    </Stack>
                </DrawerActions>
            </Stack>
        </Box>
    );
};
